/**
 * Content Suggester API Client
 *
 * Always routes through the API Gateway Lambda (kickoff + polling pattern).
 * Set REACT_APP_CONTENT_SUGGESTER_URL to override (e.g. for local Flask dev).
 */

// Resolves to https://api.snorkle.io/v1 in both dev and prod.
// Override with REACT_APP_CONTENT_SUGGESTER_URL=http://localhost:5000/api/v1 to use local Flask.
const API_BASE_URL =
  process.env.REACT_APP_CONTENT_SUGGESTER_URL ||
  process.env.REACT_APP_API_ENDPOINT ||
  'http://localhost:5000/api/v1';

// Only use the synchronous Flask path when explicitly pointed at localhost.
const isLocalFlask = () => API_BASE_URL.includes('localhost');

// ---------------------------------------------------------------------------
// Async job polling helper (production only)
// ---------------------------------------------------------------------------

/**
 * Poll /content-suggester/status/{jobId} every 5 seconds until the job
 * reaches COMPLETED or FAILED, or times out after ~10 minutes.
 */
const pollJobStatus = async (jobId, intervalMs = 5000, maxAttempts = 120) => {
  const statusUrl = `${API_BASE_URL}/content-suggester/status/${jobId}`;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise(resolve => setTimeout(resolve, intervalMs));

    const resp = await fetch(statusUrl);
    if (!resp.ok) {
      throw new Error(`Status poll failed with HTTP ${resp.status}`);
    }

    const data = await resp.json();

    if (data.status === 'COMPLETED') {
      return data;
    }
    if (data.status === 'FAILED') {
      throw new Error(data.errorMessage || 'Content generation failed.');
    }
    // PROCESSING — keep polling
  }

  throw new Error('Content generation timed out after 10 minutes.');
};

/**
 * Map educational levels from UI to backend format
 */
const mapEducationalLevel = (level) => {
  const mapping = {
    'elementary': 'elementary school (ages 6-11)',
    'middle': 'middle school (ages 11-14)',
    'high': 'high school (ages 14-18)',
    'undergraduate': 'undergraduate college students',
    'graduate': 'graduate students and researchers',
    'professional': 'medical/scientific professionals',
    'general': 'general public with high school education',
    'researcher': 'active researchers and specialists'
  };
  return mapping[level] || 'general public with high school education';
};

/**
 * Generate scrollytelling content based on literature research.
 * Uses full RAG pipeline with paper retrieval.
 *
 * Production path (API Gateway):
 *   POST /content-suggester/suggest  → { jobId }
 *   GET  /content-suggester/status/{jobId}  (polled every 5s until COMPLETED)
 *
 * Local dev path (Flask on localhost):
 *   POST /suggest  (synchronous, returns result directly)
 */
export const generateScrollytellingContent = async (config) => {
  const {
    missionStatement,
    institutionProfile,
    specificPrompt,
    educationalLevel,
    numPapers = 10,
    numSlides = 5,
    outputLanguage = 'en'
  } = config;

  const payload = {
    mission: missionStatement,
    institution_profile: institutionProfile || '',
    specific_prompt: specificPrompt || '',
    content_type: 'scrollytelling',
    domain: 'biomedical',
    num_papers: numPapers,
    num_slides: numSlides,
    audience_level: mapEducationalLevel(educationalLevel),
    output_language: outputLanguage,
    llm_provider: 'openai'
  };

  // ── Local Flask: synchronous direct call ──────────────────────────────────
  if (isLocalFlask()) {
    const response = await fetch(`${API_BASE_URL}/suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate scrollytelling content');
    }
    return await response.json();
  }

  // ── Production API Gateway: async kickoff + polling ───────────────────────
  const kickoffResponse = await fetch(`${API_BASE_URL}/content-suggester/suggest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!kickoffResponse.ok) {
    const error = await kickoffResponse.json();
    throw new Error(error.error || 'Failed to start content generation job');
  }

  const { jobId } = await kickoffResponse.json();
  if (!jobId) throw new Error('Kickoff response missing jobId');

  // Poll until COMPLETED or FAILED
  return await pollJobStatus(jobId);
};

/**
 * Generate simple content idea (no literature research)
 * Uses lightweight LLM-based generation
 * 
 * For: standalone visuals, animations, tours, presentations
 */
export const generateSimpleContent = async (config) => {
  const {
    missionStatement,
    institutionProfile,
    specificPrompt,
    contentType,
    educationalLevel,
    preferences
  } = config;

  // Build context based on selected preferences
  let mission = '';
  let institution = '';
  let prompt = '';

  if (preferences?.useMissionStatement && missionStatement) {
    mission = missionStatement;
  }
  if (preferences?.useInstitutionProfile && institutionProfile) {
    institution = institutionProfile;
  }
  if (preferences?.useSpecificPrompt && specificPrompt) {
    prompt = specificPrompt;
  }

  // For now, call the existing /suggest endpoint with content_type
  // TODO: Update to call /suggest-simple once backend endpoint is added
  const response = await fetch(`${API_BASE_URL}/suggest-simple`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mission,
      institution_profile: institution,
      specific_prompt: prompt,
      content_type: contentType,
      audience_level: mapEducationalLevel(educationalLevel),
      llm_provider: 'openai'
    })
  });

  if (!response.ok) {
    // Fallback to mock response if endpoint doesn't exist yet
    if (response.status === 404) {
      return generateMockSimpleContent(config);
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate content');
  }

  return await response.json();
};

/**
 * Mock response for simple content generation
 * Used until backend endpoint is implemented
 */
const generateMockSimpleContent = (config) => {
  const { contentType, missionStatement, specificPrompt } = config;
  
  const mockContent = {
    'standalone_visual': `**Visual Concept: Brain Connectivity Network**

1. **Visual Type**: Interactive 3D network diagram showing neural pathways
2. **Key Message**: How different brain regions communicate during complex tasks
3. **Visual Elements**:
   - Color-coded nodes representing brain regions
   - Animated connections showing signal flow
   - Intensity indicators for activity levels
4. **Title**: "The Connected Brain: Mapping Neural Highways"
5. **Caption**: "Real-time visualization of how your brain regions work together during problem-solving"

**Why It Works**: Combines scientific accuracy with visual appeal, making abstract concepts tangible.`,

    'animation': `**Animation Concept: "Journey Through the Cell"**

**Duration**: 75 seconds

**Scene Breakdown**:
1. **Opening (0-10s)**: Zoom from tissue level into single cell
2. **Inside the Cell (10-30s)**: Tour of organelles with simple analogies
3. **Molecular Action (30-60s)**: Show protein synthesis process
4. **Conclusion (60-75s)**: Zoom out to show millions of cells working in harmony

**Visual Style**: Clean 3D animation with soft colors, smooth camera movements
**Narration Focus**: Use everyday analogies (cell as factory, DNA as blueprint)
**Impact**: Helps audience visualize microscopic processes they can't see`,

    'tour': `**Interactive Tour Concept: "Inside a Synapse"**

**Tour Environment**: 3D space between two neurons at synaptic junction

**Key Stops**:
1. **Presynaptic Terminal**: Examine vesicles filled with neurotransmitters
2. **Synaptic Cleft**: Navigate the tiny gap, see neurotransmitter molecules
3. **Receptor Sites**: Interactive binding demonstration
4. **Signal Propagation**: Follow electrical signal down axon

**Interactive Elements**:
- Click neurotransmitter molecules to see chemical structure
- Rotate receptors to examine 3D shape
- Trigger signal to watch cascade effect

**Learning Arc**: Start with big picture → zoom to molecular detail → see complete process`,

    'presentation': `**Presentation Theme: "From Discovery to Impact"**

**Slide Structure** (10 slides):
1. **Hook**: Surprising statistic or question
2. **The Challenge**: Problem your research addresses
3. **Discovery Path**: Key moments in the research journey
4. **The Breakthrough**: Main finding with compelling visual
5. **How It Works**: Mechanism explained simply
6. **Data Speaks**: Key results in clear charts
7. **Real-World Impact**: Applications and benefits
8. **What's Next**: Future directions
9. **Call to Action**: How audience can engage
10. **Thank You**: Contact and resources

**Visual Strategy**: 
- One key visual per slide
- Minimal text (5-7 words max per bullet)
- Use before/after comparisons
- Include human element photos

**Story Flow**: Question → Investigation → Discovery → Impact`
  };

  return {
    success: true,
    content: mockContent[contentType] || mockContent['standalone_visual'],
    content_type: contentType,
    metadata: {
      is_mock: true,
      note: 'This is a mock response. Connect to backend for AI-generated content.',
      content_type: contentType
    }
  };
};

/**
 * Get AI-suggested search term for visual database
 * Uses lightweight LLM extraction (no paper retrieval) for speed
 */
export const suggestVisualSearchTerm = async (config) => {
  const {
    missionStatement,
    specificPrompt,
    llmProvider = 'openai'
  } = config;

  const response = await fetch(`${API_BASE_URL}/suggest-visual-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mission: missionStatement || '',
      specific_prompt: specificPrompt || '',
      llm_provider: llmProvider
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate search term');
  }

  return await response.json();
};

/**
 * Health check for backend connection
 */
export const checkBackendHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return await response.json();
  } catch (error) {
    return { status: 'unavailable', error: error.message };
  }
};
